import { ChannelType } from '@aaa-ncnu-ie/mypolicy-api-registrations';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '..';
import { useSendCodeMutation } from '../../../../../api/registration/otvc/sendCode';
import { useValidateCodeMutation } from '../../../../../api/registration/otvc/verifyCode';
import Verification from '../../../../../assets/icons/Verification';
import ControlledTextField from '../../../../../components/TextField/ControlledTextField';
import Button from '../../../../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../../../../components/styledComponents/Link';
import colors from '../../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../../store/SnackbarProvider';
import SplitView from '../../../SplitView';
import {
  OneTimeVerificationEmail,
  oneTimeVerificationEmailSchema,
} from '../../schemas/oneTimeVerificationCode';

const obfuscateUserEmail = (email?: string) => {
  if (!email) {
    return '';
  }

  const topLevelDomainIndex = email.lastIndexOf('.');
  const topLevelDomain = email.substring(topLevelDomainIndex, email.length);
  const remainingCharacterCount = email.length - topLevelDomain.length - 1; // full email length - TLD (.com, .io, etc) - 1 for the first character
  return `${email[0]}${'*'.repeat(remainingCharacterCount)}${topLevelDomain}`;
};

const OneTimeEmailVerification = () => {
  const navigate = useNavigate();
  const { formValues, otvcValues, setOtvcValues } =
    useOrganicRegistrationForm();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();
  const { track } = useDirectAccessAnalytics();

  const sendCodeMutation = useSendCodeMutation();
  const validateCodeMutation = useValidateCodeMutation();
  const obfuscatedEmail = obfuscateUserEmail(formValues.email);

  const showResentCodeBanner = () => {
    track('User Messaged', {
      event_type: 'Messaged',
      location: 'Feature Card',
      message_type: 'Info',
      contents: 'A new code has been sent to your email.',
    });
    enqueueSnackbar({
      autoHideDuration: 10000,
      content: 'A new code has been sent to your email.',
      key: 'resent-email-code',
      variant: 'success',
    });
  };

  const showGenericErrorBanner = () => {
    track('User Messaged', {
      event_type: 'Messaged',
      location: 'Feature Card',
      message_type: 'Error',
      contents: 'We are unable to process your request right now.',
    });
    enqueueSnackbar({
      key: 'policy-search-organic-registration-error',
      variant: 'error',
      title: 'We are unable to process your request right now.',
      content: (
        <>
          Please check the details and try again, or contact Service at {}
          <LinkWithNoRouter
            href="tel:+888.980.5650"
            underline="none"
            sx={{
              fontSize: {
                mobile: 16,
                tablet: 18,
              },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaSpectrumBlue50,
            }}
          >
            888.980.5650
          </LinkWithNoRouter>
          .
        </>
      ),
    });
  };

  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setValue,
  } = useForm<OneTimeVerificationEmail>({
    resolver: yupResolver(oneTimeVerificationEmailSchema), // placeholder phone number that will come from API
    mode: 'onBlur',
  });

  const requestCode = async () => {
    const { email } = formValues;
    closeSnackbar();
    try {
      await sendCodeMutation.mutateAsync({
        channelType: ChannelType.EMAIL,
        channelValue: email,
      });

      if (!otvcValues?.codeSent) {
        setOtvcValues({ ...otvcValues, codeSent: true, flow: 'Email' });
      }
      showResentCodeBanner();
    } catch (err) {
      showGenericErrorBanner();
    }
  };

  const verifyCode = async (verificationCode: string) => {
    closeSnackbar();
    const { email } = formValues;
    try {
      const res = await validateCodeMutation.mutateAsync({
        id: email,
        otp: verificationCode.toString(),
      });
      if (res.otpValidated) {
        navigate('/register/code/verification', { replace: true });
      } else {
        setValue('verificationCode', '');
        showGenericErrorBanner();
      }
    } catch (err) {
      showGenericErrorBanner();
    }
  };

  const trackValidationErrors = (fieldName?: string) => {
    if (fieldName) {
      if (fieldName in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'OTVC',
          field_name: fieldName,
          error_message:
            errors[fieldName as keyof OneTimeVerificationEmail]!.message,
          status: 'Failed',
        });
      }
    } else {
      for (const errorField in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'OTVC',
          field_name: errorField,
          error_message:
            errors[errorField as keyof OneTimeVerificationEmail]!.message,
          status: 'Failed',
        });
      }
    }
  };

  const trackInputBlurred = (fieldName?: string) => {
    const values = getValues();
    if (fieldName && values[fieldName as keyof OneTimeVerificationEmail]) {
      track('Field Information Entered', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        registration_type: 'OTVC',
        field_name: fieldName,
        input: values[fieldName as keyof OneTimeVerificationEmail]!,
      });
    }
  };

  const inputBlurred = (fieldName?: string) => {
    trackValidationErrors(fieldName);
    trackInputBlurred(fieldName);
  };

  const disableContinue = !isValid || validateCodeMutation.isPending;

  useEffect(() => {
    track('Verification Method Selected', {
      event_type: 'Option Selected',
      location: 'Feature Card',
      registration_type: 'OTVC',
      flow: 'Email',
      email: formValues.email,
      login_method: 'DA',
    });

    if (!otvcValues?.emailEligible || !formValues?.email) {
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'OTVC',
        flow: 'Email',
        reason: 'Not eligible',
        login_method: 'DA',
      });
      navigate('/register', { replace: true });
    } else if (!otvcValues?.codeSent) {
      // if the code has already been sent then we won't auto send it again, the user needs to request it manually
      requestCode();
    }
  }, []);

  const onSubmit = async ({ verificationCode }: OneTimeVerificationEmail) => {
    await verifyCode(verificationCode);
  };

  return (
    <SplitView data-testid="organic-registration-1" Icon={<Verification />}>
      <Stack
        justifyContent={{ mobile: 'flex-start', desktop: 'center' }}
        direction="column"
        sx={{ px: { mobile: 2, tablet: 14, desktop: 11 }, height: '100%' }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Verification code
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: 1,
            mb: 2,
            color: colors.csaaDark,
          }}
        >
          Check your email for the code. The code is valid for 30 minutes.
        </Typography>
        <Stack direction="column" spacing={1} sx={{ mt: 3 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              color: colors.csaaDark,
            }}
          >
            If you don't receive the code, check your spam/junk folders or{' '}
            <Button
              disableRipple
              disableTouchRipple
              variant="text"
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                fontFamily: 'inherit',
                px: 0,
                py: 0,
                width: 'fit-content',
                alignSelf: 'flex-start',
              }}
              type="button"
              onClick={async () => {
                track('Verification Code Resent', {
                  event_type: 'Link Accessed',
                  location: 'Feature Card',
                  registration_type: 'OTVC',
                  flow: 'Email',
                  email: formValues.email,
                });
                await requestCode();
              }}
            >
              send a new code now
            </Button>
            .
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              color: colors.csaaDark,
            }}
          >
            Enter the code we sent to {obfuscatedEmail}.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '24px',
              color: colors.csaaDark,
            }}
          >
            Keep this page open to enter your code. If you close it, you'll need
            to start over with a new code.
          </Typography>
        </Stack>
        <ControlledTextField
          name="verificationCode"
          control={control}
          className="csaa-qm-block"
          label="Code"
          sx={{ mt: 3 }}
          preventWhitespace
          preventNonNumericValues
          handleBlur={(e, onBlur) => {
            inputBlurred('verificationCode');
            return onBlur(e);
          }}
          inputProps={{
            autoComplete: 'one-time-code',
            'data-testid': 'otvc-email-code-field',
            maxLength: 6,
          }}
          InputProps={{
            sx: { background: colors.csaaWhite },
            required: true,
          }}
        />
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{ mt: { mobile: 3, tablet: 5 } }}
        >
          <Button
            type="submit"
            data-testid="otvc-continue-button"
            onClick={() => {
              window.scrollTo(0, 0);
            }}
            disabled={disableContinue}
            loading={validateCodeMutation.isPending}
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    </SplitView>
  );
};

export default OneTimeEmailVerification;
